// ============================================
// SENTRY ERROR TRACKING (must be first!)
// ============================================
import * as Sentry from "@sentry/react";

// Initialize Sentry before any other code runs
// This captures errors that occur during app initialization
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || "", // Set VITE_SENTRY_DSN in Vercel env vars
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD, // Only enable in production
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // Capture 10% of sessions
  replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
});

// ============================================
// DEVICE CONTEXT FOR SENTRY
// ============================================

// Add device context to help debug mobile-specific issues
Sentry.setContext("device", {
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  isStandalone: window.matchMedia('(display-mode: standalone)').matches,
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  hasVisualViewport: !!window.visualViewport,
  language: navigator.language,
  onLine: navigator.onLine,
});

// Track navigation as breadcrumbs for debugging user flows
window.addEventListener('hashchange', () => {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigated to ${window.location.hash}`,
    level: 'info',
  });
});

// Capture unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason, {
    tags: { type: 'unhandled-rejection' },
  });
});

// Track online/offline status changes
window.addEventListener('online', () => {
  Sentry.addBreadcrumb({
    category: 'network',
    message: 'Device went online',
    level: 'info',
  });
});

window.addEventListener('offline', () => {
  Sentry.addBreadcrumb({
    category: 'network',
    message: 'Device went offline',
    level: 'warning',
  });
});

import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/globals.css"; // Mobile viewport and app shell styles
import "./lib/i18n"; // Initialize i18n
import { initViewportHeight } from "./hooks/useViewportHeight";

// ============================================
// VIEWPORT HEIGHT INITIALIZATION
// ============================================

/**
 * Initialize viewport height CSS custom properties immediately
 * This runs before React renders to prevent layout shift
 */
const initializeViewport = () => {
  // Use visualViewport if available (more accurate on mobile)
  const vh = window.visualViewport?.height ?? window.innerHeight;
  
  // Set CSS custom properties
  document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
  document.documentElement.style.setProperty('--app-height', `${vh}px`);
  
  // Also call the exported init function for consistency
  initViewportHeight();
};

// Run immediately
initializeViewport();

/**
 * Debounced viewport update handler
 */
let viewportUpdateTimeout: ReturnType<typeof setTimeout>;
const updateViewportHeight = () => {
  clearTimeout(viewportUpdateTimeout);
  viewportUpdateTimeout = setTimeout(() => {
    const vh = window.visualViewport?.height ?? window.innerHeight;
    document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
    document.documentElement.style.setProperty('--app-height', `${vh}px`);
  }, 100);
};

// Use Visual Viewport API if available (better for mobile keyboards)
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', updateViewportHeight);
  window.visualViewport.addEventListener('scroll', updateViewportHeight);
}

// Fallback to window events
window.addEventListener('resize', updateViewportHeight);
window.addEventListener('orientationchange', () => {
  // Delay after orientation change for accurate measurements
  setTimeout(updateViewportHeight, 150);
});

// ============================================
// SERVICE WORKER REGISTRATION
// ============================================

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Check for service worker updates periodically
        // This ensures users get the latest version even if they have an old SW installed
        setInterval(() => {
          registration.update();
        }, 3600000); // Check every hour
        
        // Also check for updates immediately on page load
        registration.update();
      })
      .catch(() => {
        // Silently fail if SW registration fails
      });
  });
}

// ============================================
// PULL-TO-REFRESH PREVENTION
// ============================================

let touchStartY = 0;
let touchStartElement: Element | null = null;
let touchStartScrollTop = 0;

document.addEventListener('touchstart', (e) => {
  touchStartY = e.touches[0].clientY;
  touchStartElement = e.target as Element;
  
  // Find the scrollable container and store its scroll position
  let element: Element | null = touchStartElement;
  while (element && element !== document.body) {
    const style = window.getComputedStyle(element);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      touchStartScrollTop = element.scrollTop;
      break;
    }
    element = element.parentElement;
  }
}, { passive: true });

document.addEventListener('touchmove', (e) => {
  const touchY = e.touches[0].clientY;
  const touchDiff = touchY - touchStartY;
  
  // Find if touch is inside a scrollable element
  let element: Element | null = touchStartElement;
  let scrollableElement: Element | null = null;
  
  while (element && element !== document.body) {
    const style = window.getComputedStyle(element);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      scrollableElement = element;
      break;
    }
    element = element.parentElement;
  }
  
  // Prevent pull-to-refresh only when:
  // 1. Pulling down (touchDiff > 0)
  // 2. Either:
  //    a. Not inside a scrollable container, OR
  //    b. The scrollable container is already at the top
  const isAtTop = scrollableElement 
    ? scrollableElement.scrollTop <= 0 
    : window.scrollY === 0;
    
  const shouldPrevent = touchDiff > 0 && isAtTop && touchStartScrollTop <= 0;
  
  if (shouldPrevent) {
    e.preventDefault();
  }
}, { passive: false });

// ============================================
// OVERSCROLL PREVENTION ON BODY
// ============================================

// Prevent body from scrolling (all scrolling should happen in app-shell-content)
document.body.style.overflow = 'hidden';
document.body.style.position = 'fixed';
document.body.style.width = '100%';
document.body.style.height = '100%';

// ============================================
// INPUT FOCUS SCROLL-INTO-VIEW
// ============================================

/**
 * When an input is focused, scroll it into view after the keyboard animation.
 * This ensures the focused input is visible above the keyboard.
 */
document.addEventListener('focusin', (e) => {
  const target = e.target as HTMLElement;
  
  // Only handle input elements
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
    // Wait for keyboard animation to complete (iOS ~300ms, Android ~250ms)
    setTimeout(() => {
      // Use scrollIntoView with block: 'center' to position input in middle of visible area
      target.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }, 350);
  }
});

// ============================================
// RENDER APP
// ============================================

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ error }) => (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        fontFamily: 'system-ui, sans-serif' 
      }}>
        <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Something went wrong</h1>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
          We've been notified and are working to fix this issue.
        </p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Reload App
        </button>
        {import.meta.env.DEV && (
          <pre style={{ 
            marginTop: '2rem', 
            textAlign: 'left', 
            background: '#f3f4f6', 
            padding: '1rem',
            borderRadius: '0.5rem',
            overflow: 'auto',
            fontSize: '0.875rem'
          }}>
            {error?.toString()}
          </pre>
        )}
      </div>
    )}
    onError={(error, componentStack) => {
      console.error('Sentry ErrorBoundary caught:', error, componentStack);
    }}
  >
    <App />
  </Sentry.ErrorBoundary>
);